import { useState } from "react";
import { ChevronLeft, ChevronRight, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { HistoryFiltersBar } from "@/components/history/HistoryFiltersBar";
import { HistoryTable } from "@/components/history/HistoryTable";
import { Notice } from "@/components/links/Notice";
import {
  EMPTY_HISTORY_FILTERS,
  HISTORY_PAGE_SIZE,
  useLinkHistory,
  type HistoryFilters,
} from "@/lib/hooks/useLinkHistory";

/** Historico unificado: links do painel, do bot, do encaminhamento e da extensao. */
export function HistoricoPage(): JSX.Element {
  const [filters, setFilters] = useState<HistoryFilters>(EMPTY_HISTORY_FILTERS);
  const [page, setPage] = useState(0);
  const { data, isLoading, isFetching, error } = useLinkHistory(filters, page);
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / HISTORY_PAGE_SIZE) - 1);

  const handleFilters = (next: HistoryFilters): void => {
    setFilters(next);
    setPage(0);
  };

  return (
    <div className="max-w-7xl space-y-6">
      <PageHeader
        title="Histórico"
        subtitle="Todos os links gerados no painel, no bot, no encaminhamento e na extensão."
        actions={
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-[hsl(var(--surface-raised))] px-3 py-1.5 text-xs font-medium text-muted-foreground">
            <History size={12} />
            {total} {total === 1 ? "registro" : "registros"}
          </span>
        }
      />
      <Card>
        <CardContent className="space-y-4 pt-5">
          <HistoryFiltersBar filters={filters} onChange={handleFilters} />
          {error && <Notice tone="danger">Não foi possível carregar o histórico: {error.message}</Notice>}
          {isLoading && <HistorySkeleton />}
          {!isLoading && !error && total === 0 && <Notice tone="neutral">Nenhum link encontrado com esses filtros.</Notice>}
          {!isLoading && data && data.rows.length > 0 && <HistoryTable rows={data.rows} />}
          <HistoryPagination page={page} lastPage={lastPage} refreshing={isFetching && !isLoading} onPage={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}

function HistorySkeleton(): JSX.Element {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Carregando histórico">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="h-12 animate-pulse rounded-lg border border-border bg-[hsl(var(--surface-raised)/0.35)]" />
      ))}
    </div>
  );
}

interface HistoryPaginationProps {
  readonly page: number;
  readonly lastPage: number;
  readonly refreshing: boolean;
  readonly onPage: (page: number) => void;
}

function HistoryPagination({ page, lastPage, refreshing, onPage }: HistoryPaginationProps): JSX.Element {
  return (
    <nav className="flex items-center justify-between gap-3" aria-label="Paginação do histórico">
      <p className="text-xs text-muted-foreground">
        Página {Math.min(page, lastPage) + 1} de {lastPage + 1}
        {refreshing ? " · atualizando..." : ""}
      </p>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" disabled={page === 0} onClick={() => onPage(page - 1)}>
          <ChevronLeft size={14} />
          Anterior
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
          Próxima
          <ChevronRight size={14} />
        </Button>
      </div>
    </nav>
  );
}
